import { useState, useEffect, useCallback } from 'react';
import { garcomService } from '../../services';
import { formatCurrency } from '../../utils/format';
import './Garcom.css';

export default function Garcom() {
  const [logado, setLogado] = useState(!!localStorage.getItem('garcom_token'));
  const [garcomInfo, setGarcomInfo] = useState(() => {
    const s = localStorage.getItem('garcom_info');
    return s ? JSON.parse(s) : null;
  });
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginErro, setLoginErro] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // telas: mesas | comandas | produtos | carrinho | sucesso
  const [tela, setTela] = useState('mesas');
  const [mesas, setMesas] = useState([]);
  const [mesaSelecionada, setMesaSelecionada] = useState(null);
  const [cardapio, setCardapio] = useState(null);
  const [categoriaAtiva, setCategoriaAtiva] = useState(0);

  // Comandas da mesa
  const [comandasMesa, setComandasMesa] = useState([]);
  const [comandaSelecionada, setComandaSelecionada] = useState(null);
  const [carregandoComandas, setCarregandoComandas] = useState(false);
  const [novaComandaCartao, setNovaComandaCartao] = useState('');
  const [novaComandaBusca, setNovaComandaBusca] = useState('');
  const [novaComandaNome, setNovaComandaNome] = useState('');
  const [criandoComanda, setCriandoComanda] = useState(false);
  const [mostrarFormNova, setMostrarFormNova] = useState(false);
  const [cartoesBloqueados, setCartoesBloqueados] = useState([]);

  // Carrinho
  const [carrinho, setCarrinho] = useState([]);
  const [obs, setObs] = useState('');
  const [processando, setProcessando] = useState(false);
  const [ultimoPedido, setUltimoPedido] = useState(null);
  const [loading, setLoading] = useState(false);

  const carregarDados = useCallback(() => {
    if (!logado) return;
    setLoading(true);
    Promise.all([garcomService.listarMesas(), garcomService.listarCardapio()])
      .then(([mRes, cRes]) => {
        setMesas(mRes.data);
        setCardapio(cRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [logado]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // Atualiza mesas ao voltar para a tela de mesas
  useEffect(() => {
    if (tela === 'mesas' && logado) {
      garcomService.listarMesas().then(r => setMesas(r.data)).catch(() => {});
    }
  }, [tela, logado]);

  const fazerLogin = async (e) => {
    e.preventDefault();
    setLoginErro('');
    setLoginLoading(true);
    try {
      const { data } = await garcomService.login(loginForm.username, loginForm.password);
      localStorage.setItem('garcom_token', data.token);
      localStorage.setItem('garcom_info', JSON.stringify(data.garcom));
      setGarcomInfo(data.garcom);
      setLogado(true);
    } catch (err) {
      setLoginErro(err.response?.data?.error || 'Usuário ou senha inválidos');
    } finally {
      setLoginLoading(false);
    }
  };

  const sairDoApp = () => {
    localStorage.removeItem('garcom_token');
    localStorage.removeItem('garcom_info');
    setLogado(false);
    setGarcomInfo(null);
    setTela('mesas');
    setCarrinho([]);
    setMesaSelecionada(null);
    setComandaSelecionada(null);
  };

  // Seleciona mesa e vai para tela de comandas
  const selecionarMesa = async (mesa) => {
    setMesaSelecionada(mesa);
    setComandaSelecionada(null);
    setCarrinho([]);
    setObs('');
    setCategoriaAtiva(0);
    setMostrarFormNova(false);
    setNovaComandaNome('');
    setCarregandoComandas(true);
    setTela('comandas');
    try {
      const { data } = await garcomService.listarComandas(mesa.id);
      setComandasMesa(data);
    } catch {
      setComandasMesa([]);
    } finally {
      setCarregandoComandas(false);
    }
  };

  // Seleciona comanda e vai para produtos
  const selecionarComanda = (comanda) => {
    setComandaSelecionada(comanda);
    setCarrinho([]);
    setObs('');
    setCategoriaAtiva(0);
    setTela('produtos');
  };

  // Cria nova comanda para a mesa
  const cancelarComanda = async (comanda) => {
    if (!window.confirm(`Cancelar cartão #${comanda.numero}? Só é possível se não houver itens.`)) return;
    try {
      await garcomService.cancelarComanda(comanda.id);
      setComandasMesa(prev => prev.filter(c => c.id !== comanda.id));
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao cancelar comanda');
    }
  };

  const criarNovaComanda = async () => {
    if (!novaComandaCartao) { alert('Selecione o número do cartão'); return; }
    setCriandoComanda(true);
    try {
      const nomeCliente = novaComandaNome.trim() || null;
      const { data } = await garcomService.criarComanda({
        mesa_id: mesaSelecionada.id,
        numero_cartao: parseInt(novaComandaCartao),
        cliente_nome: nomeCliente,
      });
      const novaComanda = {
        id: data.id,
        numero: parseInt(novaComandaCartao),
        cliente_nome: nomeCliente,
        total: 0,
        status: 'aberta',
      };
      setComandasMesa(prev => [...prev, novaComanda]);
      setNovaComandaCartao('');
      setNovaComandaBusca('');
      setNovaComandaNome('');
      setMostrarFormNova(false);
      selecionarComanda(novaComanda);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar comanda');
    } finally {
      setCriandoComanda(false);
    }
  };

  const adicionarItem = (produto) => {
    if (produto.sem_estoque) return;
    setCarrinho(prev => {
      const ex = prev.find(i => i.produto_id === produto.id);
      if (ex) return prev.map(i => i.produto_id === produto.id ? { ...i, quantidade: i.quantidade + 1 } : i);
      return [...prev, {
        produto_id: produto.id,
        nome: produto.nome,
        emoji: produto.emoji || '🥟',
        preco: parseFloat(produto.preco),
        quantidade: 1,
      }];
    });
  };

  const removerItem = (produto_id) => {
    setCarrinho(prev => {
      const item = prev.find(i => i.produto_id === produto_id);
      if (!item) return prev;
      if (item.quantidade > 1) return prev.map(i => i.produto_id === produto_id ? { ...i, quantidade: i.quantidade - 1 } : i);
      return prev.filter(i => i.produto_id !== produto_id);
    });
  };

  const totalCarrinho = carrinho.reduce((s, i) => s + i.preco * i.quantidade, 0);
  const qtdCarrinho = carrinho.reduce((s, i) => s + i.quantidade, 0);
  const nomeComanda = comandaSelecionada?.cliente_nome || `Comanda #${comandaSelecionada?.numero}`;

  // Envia itens do carrinho para a comanda selecionada
  const confirmarPedido = async () => {
    if (carrinho.length === 0 || !comandaSelecionada) return;
    setProcessando(true);
    try {
      for (const item of carrinho) {
        await garcomService.adicionarItemComanda(comandaSelecionada.id, {
          produto_id: item.produto_id,
          quantidade: item.quantidade,
        });
      }
      setUltimoPedido({
        comanda: comandaSelecionada.numero,
        nome: comandaSelecionada.cliente_nome,
        mesa: mesaSelecionada.numero,
        total: totalCarrinho,
      });
      setCarrinho([]);
      setObs('');
      setTela('sucesso');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao enviar pedido');
    } finally {
      setProcessando(false);
    }
  };

  /* ─── Login ─── */
  if (!logado) {
    return (
      <div className="garcom-login">
        <div className="garcom-login-card">
          <div className="garcom-login-logo">🥟</div>
          <h1>Garçom</h1>
          <p>Parada Predileta</p>
          <form onSubmit={fazerLogin}>
            {loginErro && <div className="garcom-erro">{loginErro}</div>}
            <input
              type="text"
              placeholder="Usuário"
              value={loginForm.username}
              onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
              required
              autoComplete="username"
            />
            <input
              type="password"
              placeholder="Senha"
              value={loginForm.password}
              onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
              required
              autoComplete="current-password"
            />
            <button type="submit" disabled={loginLoading}>
              {loginLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="garcom-loading">
        <div className="garcom-spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  /* ─── Sucesso ─── */
  if (tela === 'sucesso') {
    return (
      <div className="garcom-sucesso">
        <div className="garcom-sucesso-content">
          <div className="garcom-sucesso-icone">✅</div>
          <h2>{ultimoPedido.nome || `Comanda #${ultimoPedido.comanda}`}</h2>
          <p>Mesa {ultimoPedido.mesa} — itens enviados para a cozinha!</p>
          <p style={{ color: '#27ae60', fontWeight: 700, fontSize: '1.2rem' }}>
            {formatCurrency(ultimoPedido.total)}
          </p>
          <button className="garcom-btn-primary" onClick={() => setTela('produtos')}>
            + Mais itens nesta comanda
          </button>
          <button className="garcom-btn-outline" onClick={() => selecionarMesa(mesaSelecionada)}>
            Outra comanda desta mesa
          </button>
          <button className="garcom-btn-outline" onClick={() => setTela('mesas')}>
            Ir para outra mesa
          </button>
        </div>
      </div>
    );
  }

  const categorias = cardapio?.categorias || [];
  const produtosDaCategoria = categorias[categoriaAtiva]?.produtos || [];

  /* ─── Mesas ─── */
  if (tela === 'mesas') {
    return (
      <div className="garcom-page">
        <div className="garcom-header">
          <div className="garcom-header-info">
            <span className="garcom-titulo">🪑 Mesas</span>
            {garcomInfo?.nome && <span className="garcom-nome-logado">{garcomInfo.nome}</span>}
          </div>
          <button className="garcom-sair" onClick={sairDoApp}>Sair</button>
        </div>
        <div className="garcom-mesas-grid">
          {mesas.map(m => (
            <button
              key={m.id}
              className={`garcom-mesa-card ${m.status}`}
              onClick={() => selecionarMesa(m)}
            >
              <div className="garcom-mesa-num">{m.numero}</div>
              <div className="garcom-mesa-status">{m.status}</div>
              {m.status === 'ocupada' && parseFloat(m.total_consumo) > 0 && (
                <div className="garcom-mesa-consumo">{formatCurrency(m.total_consumo)}</div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* ─── Comandas da Mesa ─── */
  if (tela === 'comandas') {
    return (
      <div className="garcom-page">
        <div className="garcom-header">
          <button className="garcom-voltar" onClick={() => setTela('mesas')}>
            ← Mesas
          </button>
          <span style={{ color: 'white', fontWeight: 700 }}>Mesa {mesaSelecionada?.numero}</span>
          <div style={{ width: 60 }} />
        </div>

        <div className="garcom-comandas-view">
          <p className="garcom-comandas-titulo">Selecione a comanda:</p>

          {carregandoComandas && (
            <div style={{ textAlign: 'center', padding: 32, color: '#aaa' }}>
              <div className="garcom-spinner" style={{ margin: '0 auto 12px' }}></div>
              Carregando...
            </div>
          )}

          {!carregandoComandas && comandasMesa.length === 0 && !mostrarFormNova && (
            <div className="garcom-comandas-vazio">
              <p>Nenhuma comanda aberta nesta mesa.</p>
              <p style={{ fontSize: 13, color: '#aaa' }}>Crie uma nova comanda abaixo.</p>
            </div>
          )}

          {!carregandoComandas && comandasMesa.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
              <button
                className="garcom-comanda-card"
                style={{ flex: 1 }}
                onClick={() => selecionarComanda(c)}
              >
                <div className="garcom-comanda-nome">
                  <span style={{ fontSize: 11, color: '#aaa', display: 'block', marginBottom: 2 }}>
                    🎫 Cartão #{c.numero}
                  </span>
                  {c.cliente_nome || '—'}
                </div>
                <div className="garcom-comanda-total">
                  {parseFloat(c.total) > 0 ? formatCurrency(c.total) : 'Vazia'}
                </div>
                <div className="garcom-comanda-seta">→</div>
              </button>
              {parseFloat(c.total) === 0 && (
                <button
                  onClick={() => cancelarComanda(c)}
                  style={{
                    background: '#fce4ec', color: '#c62828', border: '2px solid #ef9a9a',
                    borderRadius: 12, padding: '0 14px', fontSize: 18, cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  title="Cancelar comanda vazia"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          {/* Formulário nova comanda */}
          {mostrarFormNova ? (
            <div className="garcom-nova-comanda-form">
              {/* Número do cartão — obrigatório */}
              <label style={{ fontWeight: 600, fontSize: 13, color: '#333', marginBottom: 4, display: 'block' }}>
                🎫 Nº do cartão <span style={{ color: '#e63946' }}>*</span>
              </label>
              <input
                type="number"
                min="1" max="200"
                placeholder="Buscar cartão (1–200)..."
                value={novaComandaBusca}
                onChange={e => {
                  setNovaComandaBusca(e.target.value);
                  setNovaComandaCartao('');
                }}
                autoFocus
                style={{ marginBottom: 4 }}
              />
              {/* Lista de cartões filtrada */}
              {novaComandaCartao === '' && (
                <div style={{
                  maxHeight: 160, overflowY: 'auto',
                  border: '2px solid #e0e0e0', borderRadius: 8,
                  marginBottom: 10, background: '#fafafa',
                }}>
                  {Array.from({ length: 200 }, (_, i) => i + 1)
                    .filter(n => !novaComandaBusca || String(n).includes(novaComandaBusca))
                    .filter(n => !cartoesBloqueados.includes(n))
                    .map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => { setNovaComandaCartao(String(n)); setNovaComandaBusca(String(n)); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 14px', background: 'none', border: 'none',
                          borderBottom: '1px solid #eee', cursor: 'pointer', fontSize: 15,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        🎫 Cartão #{n}
                      </button>
                    ))
                  }
                  {Array.from({ length: 200 }, (_, i) => i + 1)
                    .filter(n => !novaComandaBusca || String(n).includes(novaComandaBusca))
                    .filter(n => !cartoesBloqueados.includes(n))
                    .length === 0 && (
                    <p style={{ padding: 12, color: '#aaa', margin: 0, fontSize: 13, textAlign: 'center' }}>
                      Nenhum cartão disponível
                    </p>
                  )}
                </div>
              )}
              {novaComandaCartao && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  background: '#e8f5e9', border: '2px solid #4caf50', borderRadius: 8,
                  marginBottom: 10,
                }}>
                  <span style={{ flex: 1, fontWeight: 700 }}>🎫 Cartão #{novaComandaCartao} selecionado</span>
                  <button type="button" onClick={() => { setNovaComandaCartao(''); setNovaComandaBusca(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 }}>
                    ✕
                  </button>
                </div>
              )}

              {/* Nome do cliente — opcional */}
              <label style={{ fontWeight: 600, fontSize: 13, color: '#333', marginBottom: 4, display: 'block' }}>
                👤 Nome do cliente <span style={{ color: '#aaa', fontWeight: 400 }}>(opcional)</span>
              </label>
              <input
                type="text"
                placeholder="Ex: João, Maria..."
                value={novaComandaNome}
                onChange={e => setNovaComandaNome(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && novaComandaCartao && criarNovaComanda()}
              />

              <div className="garcom-nova-comanda-btns" style={{ marginTop: 10 }}>
                <button
                  className="garcom-btn-outline"
                  onClick={() => { setMostrarFormNova(false); setNovaComandaCartao(''); setNovaComandaBusca(''); setNovaComandaNome(''); }}
                  disabled={criandoComanda}
                >
                  Cancelar
                </button>
                <button
                  className="garcom-btn-primary"
                  onClick={criarNovaComanda}
                  disabled={criandoComanda || !novaComandaCartao}
                  style={{ flex: 1 }}
                >
                  {criandoComanda ? 'Criando...' : 'Criar Comanda'}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="garcom-btn-nova-comanda"
              onClick={async () => {
                try {
                  const { data } = await garcomService.cartoesEmUso();
                  setCartoesBloqueados(data);
                } catch {
                  setCartoesBloqueados([]);
                }
                setMostrarFormNova(true);
              }}
            >
              + Nova Comanda
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ─── Produtos ─── */
  if (tela === 'produtos') {
    return (
      <div className="garcom-page">
        <div className="garcom-header">
          <button className="garcom-voltar" onClick={() => setTela('comandas')}>
            ← {nomeComanda}
          </button>
          {qtdCarrinho > 0 && (
            <button className="garcom-ver-carrinho" onClick={() => setTela('carrinho')}>
              🛒 {qtdCarrinho} — {formatCurrency(totalCarrinho)}
            </button>
          )}
        </div>

        <div className="garcom-comanda-badge">
          📋 {nomeComanda} · Mesa {mesaSelecionada?.numero}
        </div>

        <div className="garcom-cats">
          {categorias.map((cat, i) => (
            <button
              key={cat.nome}
              className={`garcom-cat-btn ${categoriaAtiva === i ? 'active' : ''}`}
              onClick={() => setCategoriaAtiva(i)}
            >
              {cat.emoji} {cat.nome}
            </button>
          ))}
        </div>

        <div className="garcom-produtos-list">
          {produtosDaCategoria.map(p => {
            const noCarrinho = carrinho.find(i => i.produto_id === p.id);
            return (
              <div key={p.id} className={`garcom-produto-item${p.sem_estoque ? ' produto-esgotado' : ''}`}>
                <div className="garcom-prod-emoji">{p.emoji || '🥟'}</div>
                <div className="garcom-prod-info">
                  <div className="garcom-prod-nome">{p.nome}</div>
                  <div className="garcom-prod-preco">{formatCurrency(p.preco)}</div>
                </div>
                <div className="garcom-prod-ctrl">
                  {p.sem_estoque ? (
                    <span className="produto-esgotado-badge">Esgotado</span>
                  ) : (
                    <>
                      {noCarrinho && (
                        <>
                          <button className="garcom-qty-btn" onClick={() => removerItem(p.id)}>−</button>
                          <span className="garcom-qty-num">{noCarrinho.quantidade}</span>
                        </>
                      )}
                      <button className="garcom-add-btn" onClick={() => adicionarItem(p)}>+</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {produtosDaCategoria.length === 0 && (
            <p style={{ textAlign: 'center', color: '#aaa', padding: '40px 16px' }}>
              Nenhum produto nesta categoria
            </p>
          )}
        </div>

        {qtdCarrinho > 0 && (
          <button className="garcom-btn-float" onClick={() => setTela('carrinho')}>
            🛒 Ver pedido — {formatCurrency(totalCarrinho)}
          </button>
        )}
      </div>
    );
  }

  /* ─── Carrinho ─── */
  return (
    <div className="garcom-page">
      <div className="garcom-header">
        <button className="garcom-voltar" onClick={() => setTela('produtos')}>
          ← Continuar adicionando
        </button>
      </div>

      <div className="garcom-carrinho-view">
        <h2>Pedido — {nomeComanda}</h2>
        <p style={{ color: '#888', marginBottom: 16, fontSize: 14 }}>Mesa {mesaSelecionada?.numero}</p>

        {carrinho.map(item => (
          <div key={item.produto_id} className="garcom-carrinho-item">
            <span className="gc-emoji">{item.emoji}</span>
            <span className="gc-nome">{item.nome}</span>
            <div className="garcom-prod-ctrl">
              <button className="garcom-qty-btn" onClick={() => removerItem(item.produto_id)}>−</button>
              <span className="garcom-qty-num">{item.quantidade}</span>
              <button className="garcom-add-btn" onClick={() => adicionarItem({ id: item.produto_id, nome: item.nome, emoji: item.emoji, preco: item.preco })}>+</button>
            </div>
            <span className="gc-subtotal">{formatCurrency(item.preco * item.quantidade)}</span>
          </div>
        ))}

        <div className="garcom-total-row">
          <span>Total do pedido</span>
          <strong>{formatCurrency(totalCarrinho)}</strong>
        </div>

        <textarea
          className="garcom-obs"
          placeholder="Observações (ex: sem cebola, bem passado...)"
          value={obs}
          onChange={e => setObs(e.target.value)}
          rows={3}
        />

        <button
          className="garcom-btn-primary"
          onClick={confirmarPedido}
          disabled={processando || carrinho.length === 0}
        >
          {processando ? 'Enviando...' : '✅ Enviar para Cozinha'}
        </button>

        <button className="garcom-btn-outline" onClick={() => setTela('produtos')}>
          ← Continuar adicionando
        </button>
      </div>
    </div>
  );
}
